import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DataExportButton from "../../components/DataExportButton";

import { OperationRecord } from "../types";

interface OperationDetailsDialogProps {
  open: boolean;
  record: OperationRecord | null;
  onClose: () => void;
}

const Detail = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={800}>
      {label.toUpperCase()}
    </Typography>
    <Typography fontWeight={700}>{value ?? "-"}</Typography>
  </Stack>
);

const asText = (...values: unknown[]) =>
  values
    .map((value) => (value === null || value === undefined ? "" : String(value)))
    .find(Boolean) || "-";

const asNumber = (...values: unknown[]) => {
  const value = values.find((item) => item !== null && item !== undefined);
  const parsed = Number(value || 0);

  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value: unknown) => `Rs ${Number(value || 0).toLocaleString()}`;

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === "" ||
  value === "-" ||
  value === 0 ||
  value === "Rs 0";

const displayStatus = (record: OperationRecord) => {
  if (record.type === "transfers") {
    if (record.status === "PENDING_DISPATCH" || record.status === "DRAFT") return "REQUESTED";
    if (record.status === "RECEIVED") return "COMPLETED";
  }

  return record.status;
};

const OperationDetailsDialog = ({
  open,
  record,
  onClose,
}: OperationDetailsDialogProps) => {
  if (!record) return null;

  const rawItems = Array.isArray(record.raw.items)
    ? (record.raw.items as Record<string, unknown>[])
    : record.type === "spareUsage"
      ? [record.raw]
      : [];
  const rawTicket = (record.raw.ticket || {}) as Record<string, unknown>;
  const isInvoice = record.type === "invoices";
  const isReceipt = record.type === "receipts";
  const isTransfer = record.type === "transfers";
  const rawSpareUsages = (
    Array.isArray(rawTicket.spareUsages) ? rawTicket.spareUsages : []
  ) as Record<string, unknown>[];
  const invoiceSpareItems = rawSpareUsages.map((usage) => {
    const sparePart = (usage.sparePart || {}) as Record<string, unknown>;
    const quantity = asNumber(usage.quantityUsed);
    const unitCost = asNumber(usage.unitCost);

    return {
      id: String(usage.id),
      type: "Spare Part",
      name: asText(sparePart.name, usage.sparePartId),
      code: asText(sparePart.code, sparePart.partNumber),
      quantity,
      unitCost,
      amount: quantity * unitCost,
      remarks: asText(usage.remarks),
    };
  });
  const invoiceCostRows = [
    ...invoiceSpareItems,
    {
      id: "labour-charge",
      type: "Repair / Labour",
      name: "Actual Repair Cost",
      code: "-",
      quantity: 1,
      unitCost: asNumber(record.raw.labourCharge),
      amount: asNumber(record.raw.labourCharge),
      remarks: asText(record.raw.remarks),
    },
  ].filter((item) => item.amount > 0);
  const invoiceSpareCharge = asNumber(record.raw.spareCharge);
  const invoiceLabourCharge = asNumber(record.raw.labourCharge);
  const invoiceDiscount = asNumber(record.raw.discountAmount);
  const invoiceTax = asNumber(record.raw.taxAmount);
  const invoiceTotal = asNumber(record.raw.totalAmount);
  const items = rawItems.map((item) => {
    const sparePart = (item.sparePart || {}) as Record<string, unknown>;
    const purchaseBillItem = (item.purchaseBillItem || {}) as Record<string, unknown>;
    const currentQuantity = asNumber(item.currentQuantity);
    const adjustedQuantity = asNumber(item.adjustedQuantity);
    const difference = asNumber(item.difference, adjustedQuantity - currentQuantity);
    const receivedQty = asNumber(item.quantityReceived);
    const unitCost = asNumber(item.unitCost, item.unitPrice, purchaseBillItem.unitPrice);
    const amount = asNumber(item.totalPrice, purchaseBillItem.totalPrice) || receivedQty * unitCost;

    return {
      id: String(item.id),
      spareName: asText(sparePart.name, item.spareName, item.sparePartId),
      spareCode: asText(sparePart.code, sparePart.partNumber, item.spareCode),
      requestedQty: asNumber(item.quantityRequested, item.requestedQuantity),
      dispatchedQty: asNumber(item.quantityDispatched),
      receivedQty,
      damagedQty: asNumber(item.damagedQuantity),
      missingQty: asNumber(item.missingQuantity),
      issueQty: asNumber(item.quantity, item.issuedQuantity, item.issuedQty),
      usedQty: asNumber(item.quantityUsed, item.usedQty),
      returnedQty: asNumber(item.returnedQty),
      acceptedQty: asNumber(item.acceptedQty),
      currentQuantity,
      adjustedQuantity,
      difference,
      unitCost,
      amount,
      remarks: asText(item.remarks),
    };
  });
  const detailFields = [
    { label: "Status", value: displayStatus(record), always: true },
    {
      label: "Warehouse / Hub",
      value: isTransfer ? "-" : record.warehouseLabel || record.warehouseId,
    },
    { label: "Source Hub", value: record.sourceLabel || record.source },
    { label: "Destination Hub", value: record.destinationLabel || record.destination },
    {
      label: isReceipt ? "Handled By" : "Technician",
      value: record.technicianLabel || record.technicianId,
    },
    {
      label: isTransfer ? "Transferred By" : "Created By",
      value: record.actorLabel || record.actorId,
      always: record.type === "requests" || record.type === "transfers",
    },
    { label: "Service Ticket", value: record.ticketLabel || record.ticketId },
    { label: "Vehicle", value: record.vehicleId },
    { label: "Quantity", value: record.quantity ?? 0, always: true },
    { label: "Amount", value: money(record.amount) },
    { label: "Created", value: record.createdAt ? new Date(record.createdAt).toLocaleString() : "-" },
    { label: "Updated", value: record.updatedAt ? new Date(record.updatedAt).toLocaleString() : "-" },
  ].filter((field) => field.always || !isBlank(field.value));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1} justifyContent="space-between">
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={900} sx={{ overflowWrap: "anywhere" }}>
              {record.module}: {record.code}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {record.title}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}>
          <DataExportButton kind={record.type} filters={{ recordId: record.id }} fileName={`${record.code}.pdf`} />
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {detailFields.map((field) => (
            <Grid item xs={12} md={4} key={field.label}>
              <Detail label={field.label} value={field.value} />
            </Grid>
          ))}
          {isInvoice && (
            <>
              <Grid item xs={12} md={4}>
                <Detail label="Spare Parts Cost" value={money(invoiceSpareCharge)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Detail label="Repair / Labour Cost" value={money(invoiceLabourCharge)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Detail label="Total Maintenance Cost" value={money(invoiceTotal)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Detail label="Discount" value={money(invoiceDiscount)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Detail label="Tax" value={money(invoiceTax)} />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography fontWeight={900} mb={1}>
              {isInvoice ? "Cost Breakdown" : "Item Details"}
            </Typography>
            {isInvoice ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cost Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!invoiceCostRows.length && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">
                          No cost breakdown details available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {invoiceCostRows.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.type}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.code}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{money(item.unitCost)}</TableCell>
                      <TableCell align="right">{money(item.amount)}</TableCell>
                      <TableCell>{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5}>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{money(invoiceTotal)}</strong>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            ) : isReceipt ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Damaged</TableCell>
                    <TableCell align="right">Missing</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="text.secondary">No item details available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.spareName}</TableCell>
                      <TableCell>{item.spareCode}</TableCell>
                      <TableCell align="right">{item.receivedQty}</TableCell>
                      <TableCell align="right">{item.damagedQty}</TableCell>
                      <TableCell align="right">{item.missingQty}</TableCell>
                      <TableCell align="right">{item.unitCost ? money(item.unitCost) : "-"}</TableCell>
                      <TableCell align="right">{item.amount ? money(item.amount) : "-"}</TableCell>
                      <TableCell>{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : record.type === "requests" ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Requested</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary">No item details available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.spareName}</TableCell>
                      <TableCell>{item.spareCode}</TableCell>
                      <TableCell align="right">{item.requestedQty}</TableCell>
                      <TableCell>{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : record.type === "transfers" ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Dispatched</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary">No item details available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.spareName}</TableCell>
                      <TableCell>{item.spareCode}</TableCell>
                      <TableCell align="right">{item.dispatchedQty}</TableCell>
                      <TableCell align="right">{item.receivedQty}</TableCell>
                      <TableCell>{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : record.type === "issues" || record.type === "spareUsage" ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">{record.type === "issues" ? "Issued" : "Used"}</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary">No item details available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => {
                    const quantity = record.type === "issues" ? item.issueQty : item.usedQty;
                    const amount = quantity * item.unitCost;

                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item.spareName}</TableCell>
                        <TableCell>{item.spareCode}</TableCell>
                        <TableCell align="right">{quantity}</TableCell>
                        <TableCell align="right">{money(item.unitCost)}</TableCell>
                        <TableCell align="right">{money(amount)}</TableCell>
                        <TableCell>{item.remarks}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : record.type === "returns" ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Issued</TableCell>
                    <TableCell align="right">Used</TableCell>
                    <TableCell align="right">Returned</TableCell>
                    <TableCell align="right">Accepted</TableCell>
                    <TableCell align="right">Damaged</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="text.secondary">No item details available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.spareName}</TableCell>
                      <TableCell>{item.spareCode}</TableCell>
                      <TableCell align="right">{item.issueQty}</TableCell>
                      <TableCell align="right">{item.usedQty}</TableCell>
                      <TableCell align="right">{item.returnedQty}</TableCell>
                      <TableCell align="right">{item.acceptedQty}</TableCell>
                      <TableCell align="right">{item.damagedQty}</TableCell>
                      <TableCell>{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary">No item details available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.spareName}</TableCell>
                      <TableCell>{item.spareCode}</TableCell>
                      <TableCell align="right">{item.receivedQty}</TableCell>
                      <TableCell>{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default OperationDetailsDialog;
